<template>
  <div
    class="tarjeta-lavadero-swipe mb-2 mx-auto mt-2"
    :class="swipeClass"
    :style="swipeContainerStyle"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchCancel"
  >
    <div
      v-if="swipeRightAction"
      class="tarjeta-lavadero-action tarjeta-lavadero-action--right"
    >
      <div class="tarjeta-lavadero-action__content">
        <v-icon small color="white">{{ swipeRightAction.icon }}</v-icon>
        <span>{{ swipeRightAction.label }}</span>
      </div>
    </div>

    <div
      v-if="swipeLeftAction"
      class="tarjeta-lavadero-action tarjeta-lavadero-action--left"
    >
      <div class="tarjeta-lavadero-action__content">
        <span>{{ swipeLeftAction.label }}</span>
        <v-icon small color="white">{{ swipeLeftAction.icon }}</v-icon>
      </div>
    </div>

    <v-card
      class="tarjeta-lavadero"
      outlined
      :style="tarjetaStyle"
    >
      <v-card-text class="pa-2">
        <!-- Cabecera: patente + modelo + canal badge -->
        <div class="d-flex align-center justify-space-between mb-1">
          <span class="font-weight-bold text-uppercase text-body-2">
            {{ turno.patente || turno.vin || '—' }}
            <span class="font-weight-regular text-caption ml-1">— {{ turno.modelo || '—' }}</span>
          </span>
          <img
            v-if="esCanalRada"
            src="/logos/logo_rada.webp"
            alt="Rada"
            class="rada-logo"
          />
          <v-chip
            v-else
            x-small
            :color="canalColor"
            :text-color="canalTextColor"
            label
            class="text-center px-1"
          >
            {{ canalLabel }}
          </v-chip>
        </div>

        <!-- Hora (no aplica a Rada, que ya muestra pickup_time) -->
        <div v-if="turno.hora && turno.fuente !== 'rada'" class="text-caption grey--text text--darken-1 mt-1">
          <v-icon x-small class="mr-1">fas fa-clock</v-icon>
          <template v-if="turno.fuente === 'turno_cliente'">Hora del turno de servicio: </template>
          <template v-else-if="turno.fuente === 'orden_servicio'">Hora de apertura: </template>
          <template v-else-if="turno.fuente === 'entrega'">Hora de entrega: </template>{{ turno.hora }}
        </div>

        <!-- Promesa de entrega (solo orden_servicio) -->
        <div
          v-if="turno.fuente === 'orden_servicio' && turno.promesa_hora"
          class="text-caption grey--text text--darken-1 mt-1"
        >
          <v-icon x-small class="mr-1">fas fa-clock</v-icon>
          Disponible para lavadero a hs {{ turno.promesa_hora }}
        </div>

        <!-- Rada: hora de retiro del cliente (solo si no hay comentario guardado) -->
        <div
          v-if="turno.fuente === 'rada' && turno.pickup_time && !turno.comentario"
          class="text-caption amber--text text--darken-3 mt-1"
        >
          <v-icon x-small class="mr-1">fas fa-clock</v-icon>
          El cliente retira a las {{ turno.pickup_time }}
        </div>

        <!-- Lavador asignado -->
        <div
          v-if="turno.lavador_nombre"
          class="text-caption mt-1"
          :class="turno.estado_lavadero === 'finalizado' ? 'green--text text--darken-2' : 'blue--text text--darken-2'"
        >
          <v-icon x-small class="mr-1">fas fa-user-check</v-icon>
          {{ turno.lavador_nombre }}
          <v-chip
            v-if="turno.equipo"
            x-small
            label
            color="purple lighten-4"
            text-color="purple darken-4"
            class="ml-1 equipo-chip"
          >
            Equipo
          </v-chip>
        </div>

        <!-- Área -->
        <div
          v-if="turno.area_nombre"
          class="text-caption grey--text text--darken-1 mt-1"
        >
          <v-icon x-small class="mr-1">fas fa-tag</v-icon>
          {{ turno.area_nombre }}
        </div>

        <!-- VIN para Entregas -->
        <div
          v-if="turno.fuente === 'entrega' && turno.vin"
          class="text-caption blue--text text--darken-2 mt-1"
        >
          <v-icon x-small class="mr-1">fas fa-barcode</v-icon>
          VIN: {{ turno.vin }}
        </div>

        <!-- Comentario -->
        <div
          v-if="turno.comentario"
          class="text-caption mt-1"
          :class="turno.fuente === 'rada' ? 'amber--text text--darken-3' : 'grey--text text--darken-3'"
          style="line-height: 1.3;"
        >
          <v-icon x-small class="mr-1">fas fa-comment</v-icon>
          {{ turno.comentario }}
        </div>

      </v-card-text>

      <v-card-actions
        v-if="puedeModificarUnidad"
        class="px-2 pt-0 pb-2"
      >
        <v-btn
          x-small
          outlined
          color="primary"
          class="text-caption modificar-unidad-btn"
          @click.stop="$emit('modificar-unidad', turno)"
        >
          Modificar unidad
        </v-btn>
      </v-card-actions>

      <!-- Menú de 3 puntos (reasignar, cambiar horario) -->
      <v-menu
        v-if="puedeReasignar || puedeCambiarHorario"
        left
        offset-y
        :close-on-content-click="true"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            x-small
            class="menu-trigger"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon x-small>fas fa-ellipsis-v</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            v-if="puedeReasignar"
            @click="$emit('reassign', turno)"
          >
            <v-list-item-icon class="mr-2">
              <v-icon x-small>fas fa-user-edit</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-caption">Reasignar lavador</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="puedeCambiarHorario"
            @click="$emit('cambiar-horario', turno)"
          >
            <v-list-item-icon class="mr-2">
              <v-icon x-small>fas fa-clock</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-caption">Cambiar horario de retiro</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>

      <!-- Badge de fecha: esquina inferior derecha -->
      <v-chip
        v-if="turno.fecha"
        x-small
        :color="fechaBadgeColor"
        class="fecha-badge"
        label
      >
        {{ fechaBadgeTexto }}
      </v-chip>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "TarjetaLavadero",
  props: {
    turno: {
      type: Object,
      required: true,
    },
    /**
     * Indica si el usuario actual puede reasignar este turno.
     * El padre (Tablero → Kanban) decide esto basado en permisos.
     */
    puedeReasignar: {
      type: Boolean,
      default: false,
    },
    /**
     * Indica si el usuario actual puede cambiar el horario de retiro de este turno.
     * Solo aplica a turnos de Rada en estado pendiente o asignado.
     */
    puedeCambiarHorario: {
      type: Boolean,
      default: false,
    },
    /**
     * Indica si el usuario actual puede modificar la unidad de este turno Rada.
     */
    puedeModificarUnidad: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      touchStartX: 0,
      touchStartY: 0,
      touchLastDeltaX: 0,
      swipeOffsetX: 0,
      swipeIntent: false,
    };
  },
  computed: {
    tarjetaStyle() {
      const isDark = this.$vuetify.theme.dark;

      return {
        maxWidth: "280px",
        minWidth: "240px",
        position: "relative",
        "--lavadero-card-bg": isDark ? "#1e1e1e" : "#ffffff",
        "--lavadero-card-border": isDark
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(15, 23, 42, 0.08)",
        "--lavadero-card-shadow": isDark
          ? "0 10px 24px rgba(0, 0, 0, 0.35)"
          : "0 10px 24px rgba(15, 23, 42, 0.08)",
        "--lavadero-card-title-color": isDark
          ? "rgba(255, 255, 255, 0.9)"
          : "#17202a",
        "--lavadero-card-muted-color": isDark
          ? "rgba(255, 255, 255, 0.7)"
          : "inherit",
        transform: this.swipeOffsetX
          ? `translateX(${this.swipeOffsetX}px)`
          : null,
      };
    },
    swipeContainerStyle() {
      return {
        "--swipe-right-bg": this.swipeRightAction ? this.swipeRightAction.color : "transparent",
        "--swipe-left-bg": this.swipeLeftAction ? this.swipeLeftAction.color : "transparent",
      };
    },
    swipeRightAction() {
      if (this.turno.estado_lavadero === "asignado") {
        return {
          label: "Finalizar",
          icon: "fas fa-check",
          color: "#43a047",
        };
      }

      if (this.turno.estado_lavadero === "pendiente") {
        return {
          label: "Asignar",
          icon: "fas fa-user-check",
          color: "#1976d2",
        };
      }

      return null;
    },
    swipeLeftAction() {
      if (this.turno.estado_lavadero === "asignado") {
        return {
          label: "Pendiente",
          icon: "fas fa-undo",
          color: "#f9a825",
        };
      }

      if (this.turno.estado_lavadero !== "pendiente") return null;

      return {
        label: "Finalizar",
        icon: "fas fa-check",
        color: "#43a047",
      };
    },
    swipeClass() {
      return {
        "tarjeta-lavadero-swipe--swiping": this.swipeIntent,
        "tarjeta-lavadero-swipe--right": this.swipeOffsetX > 0,
        "tarjeta-lavadero-swipe--left": this.swipeOffsetX < 0,
      };
    },
    fechaBadgeTexto() {
      if (!this.turno.fecha) return "";
      const hoy = moment().format("YYYY-MM-DD");
      const manana = moment().add(1, "day").format("YYYY-MM-DD");
      if (this.turno.fecha === hoy) return "Hoy";
      if (this.turno.fecha === manana) return "Mañana";
      return moment(this.turno.fecha, "YYYY-MM-DD").format("DD/MM");
    },
    fechaBadgeColor() {
      if (!this.turno.fecha) return "grey";
      const hoy = moment().format("YYYY-MM-DD");
      const manana = moment().add(1, "day").format("YYYY-MM-DD");
      if (this.turno.fecha === hoy) return "warning";
      if (this.turno.fecha === manana) return "wpp";
      return "icon";
    },
    canalColor() {
      const map = {
        voucher: "blue",
        turno_cliente: "orange",
        orden_servicio: "deep-orange",
        rada: "purple",
        manual: "green",
        entrega: "teal",
      };
      return map[this.canalEfectivo] || "grey";
    },
    canalTextColor() {
      return "white";
    },
    canalLabel() {
      const map = {
        voucher: "Voucher",
        turno_cliente: "Cliente",
        orden_servicio: "Cliente (OS)",
        rada: "Rada",
        manual: "Manual",
        entrega: "Entregas",
      };
      return map[this.canalEfectivo] || this.canalEfectivo;
    },
    canalEfectivo() {
      const areaNombre = String(this.turno.area_nombre || "").trim().toLowerCase();
      if (this.turno.fuente === "manual" && areaNombre === "rada") return "rada";
      return this.turno.canal || this.turno.fuente;
    },
    esCanalRada() {
      return this.canalEfectivo === "rada";
    },
  },
  methods: {
    isMobileSwipeEnabled() {
      return (
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 599px)").matches
      );
    },
    onTouchStart(event) {
      if (!this.isMobileSwipeEnabled()) return;
      if (!event.touches || event.touches.length !== 1) return;

      const touch = event.touches[0];
      event.stopPropagation();
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchLastDeltaX = 0;
      this.swipeOffsetX = 0;
      this.swipeIntent = false;
    },
    onTouchMove(event) {
      if (!this.touchStartX || !event.touches || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (!this.swipeIntent && absX > 14 && absX > absY * 1.25) {
        this.swipeIntent = true;
      }

      if (!this.swipeIntent) return;

      if (!this.getSwipeAction(deltaX > 0 ? "right" : "left")) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.touchLastDeltaX = deltaX;
      this.swipeOffsetX = Math.max(-116, Math.min(116, deltaX * 0.62));
    },
    onTouchEnd(event) {
      if (!this.swipeIntent) {
        this.resetSwipeState();
        return;
      }

      if (event) {
        event.stopPropagation();
      }

      const threshold = 76;
      const deltaX = this.touchLastDeltaX;

      if (Math.abs(deltaX) >= threshold) {
        const direction = deltaX > 0 ? "right" : "left";
        if (!this.getSwipeAction(direction)) {
          this.resetSwipeState();
          return;
        }

        const payload = {
          turno: this.turno,
          direction,
        };

        this.resetSwipeState();
        this.$nextTick(() => {
          this.$emit("swipe-action", payload);
        });
        return;
      }

      this.resetSwipeState();
    },
    onTouchCancel() {
      this.resetSwipeState();
    },
    getSwipeAction(direction) {
      return direction === "right" ? this.swipeRightAction : this.swipeLeftAction;
    },
    resetSwipeState() {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchLastDeltaX = 0;
      this.swipeOffsetX = 0;
      this.swipeIntent = false;
    },
  },
};
</script>

<style scoped>
.tarjeta-lavadero-swipe {
  position: relative;
  width: 100%;
  max-width: 280px;
  min-width: 240px;
  overflow: hidden;
  border-radius: 16px;
}

.tarjeta-lavadero {
  cursor: grab;
  user-select: none;
  width: 100%;
  margin: 0 !important;
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
  z-index: 1;
}
.tarjeta-lavadero:active {
  cursor: grabbing;
}

.tarjeta-lavadero-action {
  display: none;
}

.tarjeta-lavadero-swipe--swiping .tarjeta-lavadero {
  transition: none !important;
}

.tarjeta-lavadero-swipe--right .tarjeta-lavadero {
  border-color: var(--swipe-right-bg) !important;
  box-shadow: 0 12px 28px rgba(25, 118, 210, 0.22) !important;
}

.tarjeta-lavadero-swipe--left .tarjeta-lavadero {
  border-color: var(--swipe-left-bg) !important;
  box-shadow: 0 12px 28px rgba(67, 160, 71, 0.22) !important;
}
.rada-logo {
  height: 26px;
  width: auto;
}
.fecha-badge {
  position: absolute !important;
  bottom: 4px;
  right: 4px;
}
.menu-trigger {
  position: absolute !important;
  top: 2px;
  right: 2px;
  z-index: 2;
}
.modificar-unidad-btn {
  text-transform: none !important;
  letter-spacing: normal !important;
}

/* Centrar contenido de todos los chips de canal */
.v-chip ::v-deep .v-chip__content {
  text-align: center;
  justify-content: center;
  flex: 1 1 auto;
}

@media (max-width: 599px) {
  .tarjeta-lavadero-swipe {
    touch-action: pan-y;
  }

  .tarjeta-lavadero {
    min-height: 88px;
  }

  .equipo-chip {
    height: 18px !important;
    font-size: 0.65rem !important;
    padding: 0 6px !important;
  }

  .tarjeta-lavadero-action {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    width: 58%;
    color: #ffffff;
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.035em;
    text-transform: uppercase;
    opacity: 0;
    transition: opacity 120ms ease;
  }

  .tarjeta-lavadero-action--right {
    left: 0;
    justify-content: flex-start;
    padding-left: 18px;
    background: linear-gradient(90deg, var(--swipe-right-bg), rgba(255, 255, 255, 0));
  }

  .tarjeta-lavadero-action--left {
    right: 0;
    justify-content: flex-end;
    padding-right: 18px;
    background: linear-gradient(270deg, var(--swipe-left-bg), rgba(255, 255, 255, 0));
  }

  .tarjeta-lavadero-action__content {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  }

  .tarjeta-lavadero-swipe--right .tarjeta-lavadero-action--right,
  .tarjeta-lavadero-swipe--left .tarjeta-lavadero-action--left {
    opacity: 1;
  }
}
</style>
